import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

import { Box, Button, IconButton, InputAdornment, Link, TextField, Typography } from '@mui/material'
import { getStyles } from './Styles'
import { useTheme } from '@mui/material/styles';
import { ReactSVG } from "react-svg";

import ameya_logo from "../images/ameya_logo.png"
import { IconSvg } from '../utils/globalIcons';
import { useDispatch } from 'react-redux';
import { decodeParamToken, LOCAL_TESTING_ENABLED } from '../utils';
import { useAppContext } from '../context/AppContext';
import { tostAlert } from '../utils/AlertToast';
import { IS_COMMUNITY_MODE } from '../utils';
import LoadBar from '../utils/LoadBar';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const appName = window.__ENV__.APP_NAME
const authServerUrl = window.__ENV__.APPFLYTE_AUTHENTICATION_URL
const schemaId = window.__ENV__.AMEYA_COLLECTIONS_SCHEMA_ID

function Authentication() {

    const theme = useTheme();
    const styles = getStyles(theme);
    const navigate = useNavigate()
    const location = useLocation();
    const dispatch = useDispatch();
    const { updateAuthData } = useAppContext();
    const [authType, setAuthType] = useState(LOCAL_TESTING_ENABLED ? 'root' : 'oauth');
    const [requestType, setRequestType] = useState('signin')
    const [authStep, setAuthStep] = useState("select");

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rootUserConfig, setRootUserConfig] = useState({ username: "", password: "", error: "" });

    const handleOAuthLogin = (authProvider) => {
        try {

            localStorage.clear()
            sessionStorage.clear()
            dispatch({ type: 'RESET_ALL' });

            const redirectTo = location.state?.from || "/";
            const url = new URL(redirectTo, window.location.origin);

            const externalRequestType = url.searchParams.get("request_type");
            const fileId = url.searchParams.get("file_id");
            const documentType = url.searchParams.get("document_type");
            const creditBundleId = url.searchParams.get("credit_bundle_id");
            const computedToken = decodeParamToken(fileId, documentType);
            const encodedToken = url.searchParams.get("token");

            const params = new URLSearchParams({
                app_name: appName,
                schema_id: schemaId,
                auth_provider: authProvider,
                request_type: requestType
            });

            if (computedToken === encodedToken) {
                params.append("user_file_id", fileId);
                params.append("document_type", documentType);
                params.append("user_auth_type", "external_ameya_auth")
            }

            if (externalRequestType === "external_ameya_stripe" && creditBundleId) {
                params.append("user_auth_type", "external_ameya_stripe")
                params.append("credit_bundle_id", creditBundleId)
            }

            const width = 500;
            const height = 600;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const authUrlWithParams = `${authServerUrl}/user_auth?${params.toString()}`;
            const popup = window.open(authUrlWithParams, "oauthPopup", `width=${width},height=${height},left=${left},top=${top}`);

            const interval = setInterval(() => {
                if (popup.closed) {
                    clearInterval(interval);
                    console.log("Popup closed");
                }
            }, 1000);

        } catch (error) {
            console.log(error)
        }
    };

    const handleRootUserLogin = async () => {
        const username = rootUserConfig.username.trim();
        const password = rootUserConfig.password.trim();

        if (!username || !password) {
            setRootUserConfig({ ...rootUserConfig, error: "Please enter both username and password." });
            return;
        }

        setRootUserConfig({ ...rootUserConfig, error: "" });
        setLoading(true);

        try {

            const url = `${authServerUrl}/root_user_auth/login/${schemaId}/${appName}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    username,
                    password
                })
            });
            const data = await response.json();

            const login_status = data?.login_status;
            if (login_status) {
                const token = data?.token;
                await postLogin(token);
            } else {
                setRootUserConfig({ ...rootUserConfig, error: "Login failed. Please check your credentials and try again." });
            }

        } catch (error) {
            setRootUserConfig({ ...rootUserConfig, error: "An error occurred during login. Please try again." });
            console.error('Error logging in root user:', error);
        } finally {
            setLoading(false);
        }

    }

    useEffect(() => {
        const handlePopupMessage = async (event) => {
            const app_origin = window.location.origin
            if (event.origin !== app_origin) {
                console.warn("Invalid origin:", event.origin);
                return;
            }
            if (event.data && event.data.type === "login-success") {
                const { queryParams } = event.data;
                postLogin(queryParams);
            }
        };

        window.addEventListener("message", handlePopupMessage);
        return () => {
            window.removeEventListener("message", handlePopupMessage);
        };
        //eslint-disable-next-line
    }, []);

    const postLogin = async (authResponse) => {
        try {

            const dpodToken = authResponse?.token;
            const refreshToken = authResponse?.refresh_token;

            const isExternal = authResponse?.is_external ?? false;
            const authRequestType = authResponse?.request_type ?? null;
            const userAuthType = authResponse?.user_auth_type ?? null;
            const organizationId = authResponse?.organization_id ?? null;

            const userFileId = authResponse?.file_id ?? null;
            const documentType = authResponse?.document_type ?? null;
            const creditBundleId = authResponse?.credit_bundle_id ?? null;

            const decoded_dpod_token = jwtDecode(dpodToken);
            const app_subscribed = decoded_dpod_token?.app_subscribed ?? [];
            const user_name = decoded_dpod_token?.user_name;

            if (app_subscribed.length > 0 && app_subscribed.includes(appName)) {

                localStorage.setItem('dpod-token', JSON.stringify(dpodToken));
                localStorage.setItem('refresh-token', JSON.stringify(refreshToken));
                localStorage.setItem('UserName', JSON.stringify(user_name));

                const isExternalUser = isExternal === "True";
                const isSignin = authRequestType === "signin";
                const isSignup = authRequestType === "signup";
                const hasFileData = userFileId && documentType;

                if (isExternalUser && hasFileData) {

                    if (isSignin) {
                        updateAuthData({
                            request_type: "ext_user_signin",
                            user_auth_type: userAuthType,
                            file_id: userFileId,
                            document_type: documentType
                        });

                        navigate("/home");
                        return;
                    }

                    if (isSignup && organizationId) {
                        updateAuthData({
                            request_type: "ext_user_signup",
                            user_auth_type: userAuthType,
                            organization_id: organizationId,
                            file_id: userFileId,
                            document_type: documentType
                        });

                        navigate("/onboarding", { state: { from: "login" } });
                        return;
                    }
                }

                updateAuthData({
                    request_type: "direct_user",
                    user_auth_type: userAuthType,
                    credit_bundle_id: creditBundleId
                });

                navigate("/home");

            } else {
                tostAlert("You do not have access to this application.", "error");
                localStorage.clear();
            }
        } catch (error) {
            console.error('Error decoding Token:', error);
            if (location.pathname !== '/invite-login' && location.pathname !== '/root-user') {
                navigate('/login');
            }
            localStorage.clear()
        }
    };

    return (
        <Box sx={styles.mainContainer}>

            {loading && <LoadBar />}

            <Box sx={styles.body}>
                <Box sx={styles.signinContainer}>
                    <Box padding={'20px'} display={'flex'} alignItems={'center'} flexDirection={'column'}>
                        <img src={ameya_logo} alt='ameya_logo' style={{ height: '68px', width: '200px' }} />
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, marginTop: '10px' }}>Welcome to Ameya AI Cloud</Typography>

                        {(authStep === "select" && authType === 'oauth') && (
                            <Box marginTop="30px" display="flex" flexDirection="column" gap={2}>
                                <Button
                                    sx={styles.primaryButton}
                                    onClick={() => {
                                        setRequestType("signin");
                                        setAuthStep("provider");
                                    }}
                                >
                                    <Typography sx={styles.buttonLabel}>
                                        SIGN IN
                                    </Typography>
                                </Button>

                                <Button
                                    sx={styles.secondaryButton}
                                    onClick={() => {
                                        setRequestType("signup");
                                        setAuthStep("provider");
                                    }}
                                >
                                    <Typography sx={styles.buttonLabel}>
                                        SIGN UP
                                    </Typography>
                                </Button>
                            </Box>
                        )}

                        {(authType === 'root' && IS_COMMUNITY_MODE) && (
                            <Box sx={{ ...styles.actionGroup, marginTop: '30px' }}>
                                <TextField
                                    label="Username"
                                    variant="outlined"
                                    value={rootUserConfig.username}
                                    onChange={(e) => setRootUserConfig({ ...rootUserConfig, username: e.target.value })}
                                    sx={styles.formField}
                                />
                                <TextField
                                    label="Password"
                                    variant="outlined"
                                    type={showPassword ? 'text' : 'password'}
                                    value={rootUserConfig.password}
                                    onChange={(e) => setRootUserConfig({ ...rootUserConfig, password: e.target.value })}
                                    sx={styles.formField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                    onClick={() => setShowPassword((show) => !show)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {rootUserConfig.error && (
                                    <Typography sx={{ color: 'red', fontSize: '13px', textAlign: 'center' }}>
                                        {rootUserConfig.error}
                                    </Typography>
                                )}

                                <Button
                                    sx={styles.primaryButton}
                                    onClick={handleRootUserLogin}
                                    disabled={loading}
                                >
                                    <Typography sx={styles.buttonLabel}>
                                        {loading ? 'SIGNING IN…' : 'SIGN IN'}
                                    </Typography>
                                </Button>
                            </Box>
                        )}

                        {authStep === "provider" && (
                            <Box marginTop={'30px'} display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                {authStep === "provider" && (
                                    <>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 500, marginTop: '10px' }}>
                                            {requestType === 'signin' ? 'Sign in to your Ameya account' : 'Create an account to get started'}
                                        </Typography>

                                        <Box marginTop={'20px'}>
                                            {/* Google */}
                                            <Box
                                                sx={styles.signinButton}
                                                onClick={() => handleOAuthLogin('google')}
                                            >
                                                <ReactSVG
                                                    src={IconSvg.googleIcon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width: 24px; height:24px; display: block;');
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                                    {requestType === 'signin' ? 'Sign in with Google' : 'Continue with Google'}
                                                </Typography>
                                            </Box>

                                            {/* Microsoft */}
                                            <Box
                                                sx={{ ...styles.signinButton, marginTop: '20px' }}
                                                onClick={() => handleOAuthLogin('azure')}
                                            >
                                                <ReactSVG
                                                    src={IconSvg.office365Icon}
                                                    beforeInjection={(svg) => {
                                                        svg.setAttribute('style', 'width: 24px; height:24px; display: block;');
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                                                    {requestType === 'signin' ? 'Sign in with Microsoft' : 'Continue with Microsoft'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Button
                                            sx={{ marginTop: 3 }}
                                            onClick={() => setAuthStep("select")}
                                        >
                                            Back
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}

                        {!LOCAL_TESTING_ENABLED && (<Box sx={styles.linkGroup}>
                            {[
                                IS_COMMUNITY_MODE && {
                                    label: authType === 'oauth' ? 'Sign in as root user' : 'Sign in with OAuth provider',
                                    onClick: () => setAuthType(authType === 'oauth' ? 'root' : 'oauth'),
                                },
                                {
                                    label: 'Root user configuration',
                                    onClick: () => navigate('/root-user'),
                                },
                                {
                                    label: 'Sign up with invite',
                                    onClick: () => navigate('/invite-login'),
                                },
                            ]
                                .filter(Boolean)
                                .map((link, index, links) => (
                                    <React.Fragment key={link.label}>
                                        <Typography sx={styles.linkText} onClick={link.onClick}>
                                            {link.label}
                                        </Typography>
                                        {index < links.length - 1 && <Box sx={styles.linkDivider} />}
                                    </React.Fragment>
                                ))}
                        </Box>)}

                        <Box marginTop="20px" textAlign="center">
                            <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                                By continuing, you are indicating that you accept our{" "}
                                <Link
                                    href={`${window.__ENV__.AMEYA_WEBSITE_URL}/terms`}
                                    underline="always"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Terms of Service
                                </Link>{" "}
                                and{" "}
                                <Link
                                    href={`${window.__ENV__.AMEYA_WEBSITE_URL}/privacy`}
                                    underline="always"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Privacy Policy
                                </Link>
                            </Typography>
                        </Box>

                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default Authentication;